import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import "./BoardContextMenu.scss";

export type BoardContextAction =
  | "open"
  | "openTab"
  | "favorite"
  | "share"
  | "duplicate"
  | "rename"
  | "move"
  | "delete";

export type BoardContextMenuState = {
  x: number;
  y: number;
  boardId: string;
  boardTitle: string;
  favorited: boolean;
};

type Props = {
  menu: BoardContextMenuState | null;
  onClose: () => void;
  onAction: (action: BoardContextAction, menu: BoardContextMenuState) => void;
};

type Item = {
  id: BoardContextAction;
  label: string;
  icon: ReactNode;
  danger?: boolean;
  checked?: boolean;
};

const IconOpen = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <path
      fill="currentColor"
      d="M19 19H5V5h7V3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"
    />
  </svg>
);

const IconOpenTab = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <path
      fill="currentColor"
      d="M19 19H5V5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"
    />
  </svg>
);

const IconStar = ({ filled }: { filled?: boolean }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <path
      fill="currentColor"
      d={
        filled
          ? "M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
          : "M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z"
      }
    />
  </svg>
);

const IconShare = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <path
      fill="currentColor"
      d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"
    />
  </svg>
);

const IconCopy = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <path
      fill="currentColor"
      d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"
    />
  </svg>
);

const IconRename = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <path
      fill="currentColor"
      d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z"
    />
  </svg>
);

const IconMove = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <path
      fill="currentColor"
      d="M10 9h4V6h3l-5-5-5 5h3v3zm-1 1H6V7l-5 5 5 5v-3h3v-4zm14 2-5-5v3h-3v4h3v3l5-5zm-9 3h-4v3H7l5 5 5-5h-3v-3z"
    />
  </svg>
);

const IconTrash = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
    <path
      fill="currentColor"
      d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
    />
  </svg>
);

export const BoardContextMenu = ({ menu, onClose, onAction }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  useLayoutEffect(() => {
    if (!menu || !ref.current) {
      return;
    }
    const rect = ref.current.getBoundingClientRect();
    const pad = 8;
    let x = menu.x;
    let y = menu.y;
    if (x + rect.width > window.innerWidth - pad) {
      x = Math.max(pad, window.innerWidth - rect.width - pad);
    }
    if (y + rect.height > window.innerHeight - pad) {
      y = Math.max(pad, window.innerHeight - rect.height - pad);
    }
    setPos({ x, y });
  }, [menu]);

  useEffect(() => {
    if (!menu) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    const onPointer = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const onScroll = () => onClose();
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onPointer);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPointer);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [menu, onClose]);

  if (!menu) {
    return null;
  }

  const groups: Item[][] = [
    [
      { id: "open", label: "打开", icon: <IconOpen /> },
      { id: "openTab", label: "在新标签页中打开", icon: <IconOpenTab /> },
    ],
    [
      {
        id: "favorite",
        label: menu.favorited ? "取消收藏" : "收藏",
        icon: <IconStar filled={menu.favorited} />,
        checked: menu.favorited,
      },
    ],
    [
      { id: "share", label: "分享", icon: <IconShare /> },
      { id: "duplicate", label: "创建副本", icon: <IconCopy /> },
    ],
    [
      { id: "rename", label: "重命名", icon: <IconRename /> },
      { id: "move", label: "移动文件", icon: <IconMove /> },
      {
        id: "delete",
        label: "移至回收站",
        icon: <IconTrash />,
        danger: true,
      },
    ],
  ];

  return (
    <div
      ref={ref}
      className="board-ctx"
      style={{ left: pos.x || menu.x, top: pos.y || menu.y }}
      role="menu"
    >
      {groups.map((group, gi) => (
        <div key={gi} className="board-ctx__group">
          {gi > 0 && <div className="board-ctx__sep" />}
          {group.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              className={`board-ctx__item${
                item.danger ? " board-ctx__item--danger" : ""
              }${item.checked ? " is-checked" : ""}`}
              onClick={() => {
                onAction(item.id, menu);
                onClose();
              }}
            >
              <span className="board-ctx__icon">{item.icon}</span>
              <span className="board-ctx__label">{item.label}</span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
};
